import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  Action,
  ActionCreator,
  Creator,
  MemoizedSelector,
  Store,
} from '@ngrx/store';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { ActiveTenantStore } from '@arc/arc-shared-frontend';
import {
  CheckoutStep,
  PaymentAccount,
} from '@arc/unified-payments-frontend/shared-payment';
import { PaymentFooterActions } from '@arc/unified-payments-frontend/payment-footer';
import { selectIsAutopayEligibilityCheckAllowed } from '../shared-selectors';

/**
 * Factory that creates a reusable NgRx functional effect deciding *when* a flow should
 * dispatch its checkAutopayEligibility trigger (BTPAY-7160): once the buyer lands on the
 * Confirmation step, provided the shared eligibility conditions
 * (selectIsAutopayEligibilityCheckAllowed — tenant rules, payment method, sign-in state)
 * pass, the flow-local selected payment account isn't a one-time PAD, and there's a tenant +
 * at least one buyer account number to check.
 *
 * Usage (POI, POA, international — each passes its own checkAutopayEligibility action and
 * its own flow-local selected-payment-account / buyer-account-number(s) selectors, since
 * those live in make-payment-core/pay-on-account, not in shared-stores):
 *
 *   export const poiAutopayEligibilityDispatchEffect = createAutopayEligibilityDispatchEffect({
 *     checkEligibilityAction: PoiPaymentConfirmationComponentActions.checkAutopayEligibility,
 *     selectSelectedPaymentAccount,
 *     selectAccountNumbers: selectTransactionBuyerAccounts,
 *     selectSuppressed: selectArrivedViaReceiptLanding,
 *   });
 *
 * selectSuppressed is optional — pass it wherever the flow can land on Confirmation with the
 * banner forced hidden (BTPAY-7254's Wallet "Back" receipt landing) so this doesn't run a
 * pointless eligibility check for a banner that will never show.
 */
export function createAutopayEligibilityDispatchEffect<
  A extends Action<string> & { tenantId: string; accountNumbers: string[] },
>(config: {
  checkEligibilityAction: ActionCreator<string, Creator<any[], A>>;
  selectSelectedPaymentAccount: MemoizedSelector<object, PaymentAccount>;
  selectAccountNumbers: MemoizedSelector<object, string[]>;
  selectSuppressed?: MemoizedSelector<object, boolean>;
}) {
  return createEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(PaymentFooterActions.CurrentCheckoutStepChangedAction),
        filter(
          (action) => action.payload.currentStep === CheckoutStep.Confirmation,
        ),
        // switchMap into a live combineLatest (rather than snapshotting via
        // concatLatestFrom) so a redirect-driven landing on Confirmation — e.g.
        // the intl SEPA/Twikey flow, which reaches this step via a full page
        // reload — keeps watching until payment-rules/tenant/account state
        // finishes loading, instead of judging eligibility off whatever these
        // selectors happen to hold the instant the action fires.
        switchMap(() =>
          combineLatest([
            store.select(selectIsAutopayEligibilityCheckAllowed),
            store.select(config.selectSelectedPaymentAccount),
            store.select(ActiveTenantStore),
            store.select(config.selectAccountNumbers),
            config.selectSuppressed
              ? store.select(config.selectSuppressed)
              : of(false),
          ]).pipe(
            filter(
              ([
                isEligibilityCheckAllowed,
                selectedPaymentAccount,
                tenant,
                accountNumbers,
                suppressed,
              ]) =>
                isEligibilityCheckAllowed &&
                !suppressed &&
                !selectedPaymentAccount?.isOneTimePaymentAccount &&
                !!tenant?.tenantId &&
                accountNumbers?.length > 0,
            ),
            take(1),
          ),
        ),
        map(
          ([, , tenant, accountNumbers]) =>
            config.checkEligibilityAction({
              tenantId: tenant.tenantId,
              accountNumbers,
            }) as Action,
        ),
      ),
    { functional: true },
  );
}
